import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FiArrowRight } from 'react-icons/fi';
import api from '../utils/api';

function parseProduct(row) {
  const data = JSON.parse(row.data || '{}');
  const tags = JSON.parse(row.tags || '[]');
  return {
    id: row.id,
    slug: row.slug,
    name: row.headline,
    tagline: data.tagline || '',
    company: row.company,
    role: row.role,
    platform: data.platform || '',
    version: data.version || '',
    requirements: data.requirements || '',
    tags,
    summary: row.summary,
    features: data.features || [],
    techStack: data.techStack || [],
    links: data.links || {},
  };
}


function ProductCard({ product, index }) {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.05 });
  const navigate = useNavigate();

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ delay: index * 0.05, duration: 0.2 }}
      className="card-modern overflow-hidden cursor-pointer hover:border-gray-400 dark:hover:border-gray-500 transition-colors"
      onClick={() => product.slug && navigate(`/projects/${product.slug}`)}
    >
      <div className="p-8">
        <div className="flex flex-wrap items-center gap-2 mb-2">
          <span className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
            {product.company}
          </span>
          <span className="text-gray-300 dark:text-gray-600">·</span>
          <span className="text-xs text-gray-400 dark:text-gray-500">{product.role}</span>
          {product.platform && (
            <>
              <span className="text-gray-300 dark:text-gray-600">·</span>
              <span className="text-xs text-gray-400 dark:text-gray-500">{product.platform}</span>
            </>
          )}
        </div>

        <h2 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white leading-tight mb-1">
          {product.name}
        </h2>
        {product.tagline && (
          <p className="text-base text-gray-500 dark:text-gray-400 mb-4">{product.tagline}</p>
        )}

        <div className="flex flex-wrap gap-2 mb-5">
          {product.tags.map((tag) => (
            <span
              key={tag}
              className="text-xs px-2 py-1 bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 rounded-md"
            >
              {tag}
            </span>
          ))}
        </div>

        <p className="text-gray-600 dark:text-gray-300 leading-relaxed mb-6">{product.summary}</p>

        <span className="flex items-center space-x-2 text-sm font-medium text-gray-700 dark:text-gray-300">
          <span>View project</span>
          <FiArrowRight className="w-4 h-4" />
        </span>
      </div>
    </motion.div>
  );
}

function Projects() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  useEffect(() => {
    const load = async () => {
      try {
        const response = await api.get('/projects');
        const parsed = response.data
          .filter(row => row.type === 'product')
          .map(row => parseProduct(row));
        setProducts(parsed);
      } catch (error) {
        console.error('Error loading projects:', error);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 py-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: -20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          className="mb-16"
        >
          <h1 className="text-5xl md:text-6xl font-bold mb-4 text-gray-900 dark:text-white">
            Projects
          </h1>
          <p className="text-lg text-gray-500 dark:text-gray-400 max-w-2xl">
            Products I've defined, built, and shipped.
          </p>
        </motion.div>

        {loading ? (
          <div className="flex justify-center py-20">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
              className="w-10 h-10 border-4 border-gray-300 dark:border-gray-600 border-t-black dark:border-t-white rounded-full"
            />
          </div>
        ) : (
          <div className="space-y-8">
            {products.map((product, index) => (
              <ProductCard key={product.id} product={product} index={index} />
            ))}
            {products.length === 0 && (
              <p className="text-center text-gray-400 dark:text-gray-500 py-12">No projects yet.</p>
            )}
          </div>
        )}

        <motion.div
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
          transition={{ delay: 0.6 }}
          className="mt-16 text-center"
        >
          <p className="text-gray-500 dark:text-gray-400 mb-4">
            Facing a product or operations challenge?
          </p>
          <Link to="/wait-works" className="btn-modern inline-flex items-center space-x-2">
            <span>Work With Me</span>
            <FiArrowRight className="w-4 h-4" />
          </Link>
        </motion.div>
      </div>
    </div>
  );
}

export default Projects;
